import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Heart, ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data?.detail || 'Something went wrong');
      }
    } catch (err) {
      setError(`Connection error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-purple-100/50 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-pink-100/50 rounded-full blur-[80px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Link to="/login" className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e] mb-8 transition-colors" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">Back to sign in</span>
        </Link>

        <Card className="border-[#1a1a2e]/5 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
                <Heart className="w-6 h-6 text-white" strokeWidth={2} fill="currentColor" />
              </div>
              <h1 className="text-2xl font-bold text-[#1a1a2e]">
                {sent ? 'Check your email' : 'Reset your password'}
              </h1>
              <p className="text-[#1a1a2e]/50 mt-2">
                {sent ? 'We\'ve sent you a password reset link' : 'Enter your email and we\'ll send you a reset link'}
              </p>
            </div>

            {sent ? (
              <div className="space-y-6">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-emerald-700 text-sm font-medium">Check your inbox</p>
                  <p className="text-emerald-600 text-xs mt-1">We've sent a password reset link to {email}</p>
                </div>
                <p className="text-[#1a1a2e]/40 text-xs text-center">Don't see it? Check your spam folder.</p>
                <Link to="/login">
                  <Button className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full py-6" data-testid="back-to-login-button">
                    Back to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-[#1a1a2e]/70">Email address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a1a2e]/30" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl py-6"
                      placeholder="you@example.com"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full py-6 font-medium shadow-lg"
                  data-testid="forgot-submit-button"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
